import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { Controller } from 'react-hook-form';
import { UploadFile } from '@mui/icons-material';
import { TargetPopulationUploaderProps } from '../types/study-edit.interface';
import { useParseParticipantCsv } from '@/features/studies/hooks/useParseParticipantCsv';
import { useTranslation } from 'react-i18next';
import { CreateTargetPopulationDto } from '@aphp-ema/common/target-population';
import { useNotification } from '@refinedev/core';

export const TargetPopulationUploader = ({
  control,
  errors,
  disabled,
}: TargetPopulationUploaderProps) => {
  const { t } = useTranslation();

  const { parseParticipantCsv, isLoading } = useParseParticipantCsv();
  const { open: openNotification } = useNotification();

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: CreateTargetPopulationDto) => void,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      openNotification?.({
        type: 'error',
        message: t('pages.studies.errors.notCsvFile'),
      });
      return;
    }

    const result = await parseParticipantCsv(file);

    onChange({ fileName: result.fileName, data: result.data });
  };

  return (
    <Box>
      <Controller
        name="targetPopulation"
        control={control}
        render={({ field }) => {
          const numberOfParticipants = Object.keys(
            field.value?.data || {},
          ).length;

          return (
            <Box>
              <Button
                variant="outlined"
                component="label"
                startIcon={
                  isLoading ? <CircularProgress size={20} /> : <UploadFile />
                }
                disabled={disabled || isLoading}
                fullWidth
              >
                {isLoading
                  ? t('pages.studies.actions.uploading')
                  : field.value?.fileName
                    ? `${field.value.fileName} (${t(
                        'pages.studies.fields.patient',
                        {
                          count: numberOfParticipants,
                        },
                      )})`
                    : t('pages.studies.actions.uploadTargetPopulationCsv')}
                <input
                  type="file"
                  hidden
                  accept=".csv"
                  onChange={(e) => handleFileSelect(e, field.onChange)}
                  disabled={disabled || isLoading}
                />
              </Button>
              {errors.targetPopulation && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ mt: 1, display: 'block' }}
                >
                  {errors.targetPopulation.message}
                </Typography>
              )}
            </Box>
          );
        }}
      />
    </Box>
  );
};
